/**
 * Template Editor State Atoms
 * Sprint 19: Custom Template Creation
 * 
 * Jotai atoms for managing template editor state, scene editing, and validation
 */

import { atom } from 'jotai';
import { Template, TemplateScene, TemplateScript } from '../../../types/template';
import { PROJECT_LIMITS } from '../config/projectLimits';

export const templateEditorVisibleAtom = atom<boolean>(false);

export const templateEditorModeAtom = atom<'create' | 'edit' | 'duplicate'>('create');

export const editingTemplateAtom = atom<Partial<Template> | null>(null);

export const sourceTemplateAtom = atom<Template | null>(null);

export const activeSceneIndexAtom = atom<number>(0);

export const templateIsDirtyAtom = atom<boolean>(false);

export const templateValidationErrorsAtom = atom<Record<string, string>>({});

export const isTemplateValidAtom = atom((get) => {
  const template = get(editingTemplateAtom);
  const errors = get(templateValidationErrorsAtom);
  
  if (!template) return false;
  if (Object.keys(errors).length > 0) return false;
  if (!template.title?.trim()) return false;
  if (!template.scenes || template.scenes.length === 0) return false;
  
  return true;
});

export const activeSceneAtom = atom((get) => {
  const template = get(editingTemplateAtom);
  const index = get(activeSceneIndexAtom);
  
  if (!template?.scenes || index < 0 || index >= template.scenes.length) {
    return null;
  }
  
  return template.scenes[index];
});

export const activeScriptAtom = atom((get) => {
  const template = get(editingTemplateAtom);
  const scene = get(activeSceneAtom);
  
  if (!template?.scripts || !scene?.scriptKey) return null;
  
  return template.scripts.find(s => s.key === scene.scriptKey) || null;
});

export const initializeCreateModeAtom = atom(
  null,
  (get, set) => {
    set(templateEditorModeAtom, 'create');
    set(sourceTemplateAtom, null);
    set(editingTemplateAtom, {
      title: '',
      description: '',
      category: 'content',
      aspectRatio: '16:9',
      scenes: [],
      scripts: [],
      exampleOutput: {
        title: '',
        duration: 0,
        whatUserGets: ''
      },
      createdBy: 'user',
      isOfficial: false,
      createdAt: Date.now()
    });
    set(activeSceneIndexAtom, 0);
    set(templateIsDirtyAtom, false);
    set(templateValidationErrorsAtom, {});
    set(templateEditorVisibleAtom, true);
  }
);

export const initializeDuplicateModeAtom = atom(
  null,
  (get, set, sourceTemplate: Template) => {
    set(templateEditorModeAtom, 'duplicate');
    set(sourceTemplateAtom, sourceTemplate);
    set(editingTemplateAtom, {
      ...sourceTemplate,
      id: undefined,
      title: `${sourceTemplate.title} (Copy)`,
      isOfficial: false,
      createdBy: 'user',
      createdAt: Date.now()
    });
    set(activeSceneIndexAtom, 0);
    set(templateIsDirtyAtom, true);
    set(templateValidationErrorsAtom, {});
    set(templateEditorVisibleAtom, true);
  }
);

export const initializeEditModeAtom = atom(
  null,
  (get, set, template: Template) => {
    set(templateEditorModeAtom, 'edit');
    set(sourceTemplateAtom, null);
    set(editingTemplateAtom, { ...template });
    set(activeSceneIndexAtom, 0);
    set(templateIsDirtyAtom, false);
    set(templateValidationErrorsAtom, {});
    set(templateEditorVisibleAtom, true);
  }
);

export const updateTemplateFieldAtom = atom(
  null,
  (get, set, field: keyof Template, value: any) => {
    const template = get(editingTemplateAtom);
    if (!template) return;
    
    set(editingTemplateAtom, {
      ...template,
      [field]: value,
      updatedAt: Date.now()
    });
    set(templateIsDirtyAtom, true);
    set(templateValidationErrorsAtom, {});
  }
);

export const addSceneAtom = atom(
  null,
  (get, set) => {
    const template = get(editingTemplateAtom);
    if (!template) return;
    
    const currentSceneCount = template.scenes?.length || 0;
    
    if (currentSceneCount >= PROJECT_LIMITS.MAX_SCENES_RECORDING_MODE) {
      return { success: false, reason: 'max_scenes_reached' };
    }
    
    const newSceneIndex = currentSceneCount;
    const scriptKey = `script-${newSceneIndex}-${Date.now()}`;
    
    const newScene: TemplateScene = {
      order: newSceneIndex,
      title: `Scene ${newSceneIndex + 1}`,
      suggestedDuration: 60,
      layoutPreset: 'Preset_Default',
      background: { type: 'solid', colors: ['#1F2937'] },
      scriptKey: scriptKey,
      notes: '',
      tags: []
    };
    
    const newScript: TemplateScript = {
      key: scriptKey,
      sceneTitle: newScene.title,
      content: '',
      defaultExample: '',
      customizationNote: 'Add your script content here'
    };
    
    set(editingTemplateAtom, {
      ...template,
      scenes: [...(template.scenes || []), newScene],
      scripts: [...(template.scripts || []), newScript]
    });
    set(activeSceneIndexAtom, newSceneIndex);
    set(templateIsDirtyAtom, true);
  }
);

export const removeSceneAtom = atom(
  null,
  (get, set, sceneIndex: number) => {
    const template = get(editingTemplateAtom);
    if (!template?.scenes || sceneIndex < 0 || sceneIndex >= template.scenes.length) return;
    
    const sceneToRemove = template.scenes[sceneIndex];
    const scriptKeyToRemove = sceneToRemove.scriptKey;
    
    const newScenes = template.scenes
      .filter((_, i) => i !== sceneIndex)
      .map((scene, i) => ({ ...scene, order: i }));
    
    const newScripts = (template.scripts || []).filter(s => s.key !== scriptKeyToRemove);
    
    set(editingTemplateAtom, {
      ...template,
      scenes: newScenes,
      scripts: newScripts
    });
    
    const activeIndex = get(activeSceneIndexAtom);
    if (activeIndex >= newScenes.length) {
      set(activeSceneIndexAtom, Math.max(0, newScenes.length - 1));
    }
    
    set(templateIsDirtyAtom, true);
  }
);

export const updateSceneAtom = atom(
  null,
  (get, set, sceneIndex: number, updates: Partial<TemplateScene>) => {
    const template = get(editingTemplateAtom);
    if (!template?.scenes || sceneIndex < 0 || sceneIndex >= template.scenes.length) return;
    
    const newScenes = template.scenes.map((scene, i) => 
      i === sceneIndex ? { ...scene, ...updates } : scene
    );
    
    let newScripts = template.scripts || [];
    if (updates.title) {
      const scriptKey = template.scenes[sceneIndex].scriptKey;
      const newTitle = updates.title;
      newScripts = newScripts.map(script =>
        script.key === scriptKey ? { ...script, sceneTitle: newTitle } : script
      );
    }
    
    set(editingTemplateAtom, {
      ...template,
      scenes: newScenes,
      scripts: newScripts
    });
    set(templateIsDirtyAtom, true);
    set(templateValidationErrorsAtom, {});
  }
);

export const updateScriptContentAtom = atom(
  null,
  (get, set, scriptKey: string, content: string) => {
    const template = get(editingTemplateAtom);
    if (!template?.scripts) return;
    
    const newScripts = template.scripts.map(script =>
      script.key === scriptKey ? { ...script, content } : script
    );
    
    set(editingTemplateAtom, {
      ...template,
      scripts: newScripts
    });
    set(templateIsDirtyAtom, true);
    set(templateValidationErrorsAtom, {});
  }
);

export const reorderScenesAtom = atom(
  null,
  (get, set, fromIndex: number, toIndex: number) => {
    const template = get(editingTemplateAtom);
    if (!template?.scenes) return;
    
    const scenes = [...template.scenes];
    const [removed] = scenes.splice(fromIndex, 1);
    scenes.splice(toIndex, 0, removed);
    
    const reorderedScenes = scenes.map((scene, i) => ({ ...scene, order: i }));
    
    set(editingTemplateAtom, {
      ...template,
      scenes: reorderedScenes
    });
    set(templateIsDirtyAtom, true);
  }
);

export const closeTemplateEditorAtom = atom(
  null,
  (get, set) => {
    set(templateEditorVisibleAtom, false);
    set(editingTemplateAtom, null);
    set(sourceTemplateAtom, null);
    set(templateIsDirtyAtom, false);
    set(templateValidationErrorsAtom, {});
  }
);
